import {
  DefaultLogger,
  FSAdapterInterface,
  FSDownloadOptions,
  FSStatResult,
} from "@citychallenge/core";
import axios from "axios";

import { getStorage } from "./get-storage";

/**
 * Special class that implements an object cache around session storage.
 *
 * This is because in incognito more or special browser privacy modes sessionStorage
 * may not always work as expected, and a fallback into an object cache can be
 * quite handly.
 *
 * See Sentry issue:
 * https://sentry.io/share/issue/75a141fe40a64b7a9a540e5583842ba4/
 */
class CachedSessionStorage {
  private _cache: { [key: string]: string } = {};

  public getItem(key: string) {
    const cached = this._cache[key];

    if (cached) return cached;

    try {
      return getStorage("sessionStorage").getItem(key);
    } catch (e) {
      return null;
    }
  }

  public setItem(key: string, value: string) {
    // The in-memory cache is authoritative for the lifetime of the page and is
    // therefore always written first. The sessionStorage write is best-effort:
    // it only buys us survival across a reload, and a large story bundle blows
    // the ~5MB per-origin quota (counted in UTF-16 code units). Losing that copy
    // must never take down playback, so we degrade to memory-only and report.
    this._cache[key] = value;

    try {
      getStorage("sessionStorage").setItem(key, value);
    } catch (e) {
      DefaultLogger.captureException(e as Error, "warning", (scope) => {
        scope.setExtra("key", key);
        scope.setExtra("length", value.length);
      });
    }
  }
}

export class FSAdapter implements FSAdapterInterface {
  public CachesDirectoryPath = "";

  private cachedStorage = new CachedSessionStorage();

  public async exists(filepath: string) {
    // TODO: Implement

    if (filepath.endsWith(".json")) {
      return !!this.cachedStorage.getItem(filepath);
    }

    // Just return true for now, because we have no caching implemented yet
    return true;
  }

  public async stat(filepath: string): Promise<FSStatResult> {
    // TODO: Implement
    return {
      path: filepath,
      size: "100000", // 100kb placeholder
    };
  }

  public async mkdir(_filepath: string) {
    // This is a noop in the Cache system
    return;
  }

  public downloadFile(options: FSDownloadOptions) {
    // TODO: Implement
    const cachedStorage = this.cachedStorage;
    if (options.toFile.endsWith(".json")) {
      const jobId = Math.floor(Math.random() * 9999999999);

      async function downloadJson() {
        let url = options.fromUrl;
        if (window.location.hostname === "web.intractive.eu.ngrok.io") {
          url = url.replace(
            "http://web.intractive.localhost:3003",
            "https://web.intractive.eu.ngrok.io"
          );
        }

        const { data, status, headers } = await axios.get(url);

        const jsonFile = JSON.stringify(data);

        cachedStorage.setItem(options.toFile, jsonFile);

        return {
          jobId,
          statusCode: status,
          bytesWritten: +(headers["content-length"] || 0),
        };
      }
      return {
        jobId,
        promise: downloadJson(),
      };
    }

    return {
      jobId: -1,
      promise: Promise.resolve({
        jobId: -1,
        statusCode: 200,
        bytesWritten: 10000,
      }),
    };
  }

  public async readFile(filepath: string, _encodingOrOptions?: any) {
    if (filepath.endsWith(".json")) {
      const item = this.cachedStorage.getItem(filepath);

      if (!item) throw new Error("File does not exist");

      return item;
    }
    throw new Error("Cannot read this file.");
  }

  public async unlink(_filepath: string) {
    // TODO: Implement
    return;
  }
}
